import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import userTradeSignalsAPI from '../../api/userTradeSignals';

// Async thunks
export const fetchUserTradeSignals = createAsyncThunk(
  'userTradeSignals/fetchUserTradeSignals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userTradeSignalsAPI.getUserTradeSignals(params);
      return { 
        ...response.data, 
        pageDirection: params?.pageDirection 
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trade signals');
    }
  }
);

export const fetchUserActiveTradeSignals = createAsyncThunk(
  'userTradeSignals/fetchUserActiveTradeSignals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userTradeSignalsAPI.getUserActiveTradeSignals(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch active trade signals');
    }
  }
);

export const fetchUserTradeSignalStats = createAsyncThunk(
  'userTradeSignals/fetchUserTradeSignalStats',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userTradeSignalsAPI.getUserTradeSignalStats(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trade signal stats');
    }
  }
);

export const fetchUserRecentTradeSignals = createAsyncThunk(
  'userTradeSignals/fetchUserRecentTradeSignals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userTradeSignalsAPI.getUserRecentTradeSignals(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch recent trade signals');
    }
  }
);

export const fetchUserTradeSignalsBySymbol = createAsyncThunk(
  'userTradeSignals/fetchUserTradeSignalsBySymbol',
  async ({ symbol, params = {} }, { rejectWithValue }) => {
    try {
      const response = await userTradeSignalsAPI.getUserTradeSignalsBySymbol(symbol, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trade signals by symbol');
    }
  }
);

const initialSummary = {
  total: 0,
  wins: 0,
  losses: 0,
  concluded: 0,
  winRate: 0,
  avgCurrentRR: null,
  avgPercentageGain: null
};

const initialPagination = {
  total: 0,
  cursor: null,
  limit: 50,
  hasMore: false,
  pageIndex: 0
};

const initialFilters = {
  symbol: '',
  strategy: '',
  active: '',
  fromDate: '',
  toDate: '',
  hit3R: false,
  stoppedOut: false,
  sortBy: '',
  sortOrder: 'desc',
  limit: 50,
  cursor: null
};

const initialTableStats = {
  totalSignals: 0,
  activeSignals: 0,
  inactiveSignals: 0,
  completed: 0,
  wins: 0,
  losses: 0,
  winRate: 0,
  avgRR: 0,
  totalProfitLoss: 0
};

const initialStats = {
  total: 0,
  active: 0,
  completed: 0,
  hit3R: 0,
  stoppedOut: 0,
  totalPnL: 0,
  winRate: 0,
  avgRR: 0,
  strategies: {},
  symbols: []
};

const initialState = {
  signals: [],
  activeSignals: [],
  recentSignals: [],
  stats: { ...initialStats },
  summary: { ...initialSummary },
  pagination: { ...initialPagination },
  tableStats: { ...initialTableStats },
  filters: { ...initialFilters },
  total: 0,
  symbolSignals: {},
  loading: {
    signals: false,
    activeSignals: false,
    recentSignals: false,
    stats: false,
    symbolSignals: false
  },
  error: {
    signals: null,
    activeSignals: null,
    recentSignals: null,
    stats: null,
    symbolSignals: null
  },
  lastFetch: {
    signals: null,
    activeSignals: null,
    recentSignals: null,
    stats: null,
    symbolSignals: null
  }
};

const userTradeSignalsSlice = createSlice({
  name: 'userTradeSignals',
  initialState,
  reducers: {
    clearError: (state, action) => {
      const { type } = action.payload;
      if (type && state.error[type]) {
        state.error[type] = null;
      }
    },
    clearSignals: (state) => {
      state.signals = [];
      state.activeSignals = [];
      state.recentSignals = [];
      state.symbolSignals = {};
      state.summary = { ...initialSummary };
      state.pagination = { ...initialPagination };
      state.tableStats = { ...initialTableStats };
      state.filters = { ...initialFilters };
      state.total = 0;
      state.stats = { ...initialStats };
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
      if (!Object.prototype.hasOwnProperty.call(action.payload, 'cursor')) {
        state.filters.cursor = null;
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch user trade signals
    builder
      .addCase(fetchUserTradeSignals.pending, (state) => {
        state.loading.signals = true;
        state.error.signals = null;
      })
      .addCase(fetchUserTradeSignals.fulfilled, (state, action) => {
        state.loading.signals = false;
        state.signals = action.payload.signals || [];
        state.summary = action.payload.summary
          ? { ...initialSummary, ...action.payload.summary }
          : { ...initialSummary };
        
        // Get pagination from response and merge with initial
        const responsePagination = action.payload.pagination
          ? { ...initialPagination, ...action.payload.pagination }
          : { ...initialPagination };
        
        // Track pageIndex for cursor-based pagination
        const pageDirection = action.payload.pageDirection;
        if (pageDirection === 'next') {
          responsePagination.pageIndex = (state.pagination.pageIndex || 0) + 1;
        } else if (pageDirection === 'prev' && state.pagination.pageIndex > 0) {
          responsePagination.pageIndex = state.pagination.pageIndex - 1;
        } else if (!pageDirection) {
          // Fresh load - reset to page 0
          responsePagination.pageIndex = 0;
        }
        
        state.pagination = responsePagination;
        state.tableStats = action.payload.stats
          ? { ...initialTableStats, ...action.payload.stats }
          : { ...initialTableStats };
        state.total = action.payload.total ?? state.summary.total ?? state.signals.length;
        if (action.payload.strategies) {
          state.stats.strategies = action.payload.strategies;
        }
        if (action.payload.symbols) {
          state.stats.symbols = action.payload.symbols;
        }
        state.lastFetch.signals = new Date().toISOString();
      })
      .addCase(fetchUserTradeSignals.rejected, (state, action) => {
        state.loading.signals = false;
        state.error.signals = action.payload;
      });

    // Fetch user active trade signals
    builder
      .addCase(fetchUserActiveTradeSignals.pending, (state) => {
        state.loading.activeSignals = true;
        state.error.activeSignals = null;
      })
      .addCase(fetchUserActiveTradeSignals.fulfilled, (state, action) => {
        state.loading.activeSignals = false;
        state.activeSignals = action.payload.signals || [];
        state.lastFetch.activeSignals = new Date().toISOString();
      })
      .addCase(fetchUserActiveTradeSignals.rejected, (state, action) => {
        state.loading.activeSignals = false;
        state.error.activeSignals = action.payload;
      });

    // Fetch user trade signal stats
    builder
      .addCase(fetchUserTradeSignalStats.pending, (state) => {
        state.loading.stats = true;
        state.error.stats = null;
      })
      .addCase(fetchUserTradeSignalStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.stats = action.payload;
        state.lastFetch.stats = new Date().toISOString();
      })
      .addCase(fetchUserTradeSignalStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error.stats = action.payload;
      });

    // Fetch user recent trade signals
    builder
      .addCase(fetchUserRecentTradeSignals.pending, (state) => {
        state.loading.recentSignals = true;
        state.error.recentSignals = null;
      })
      .addCase(fetchUserRecentTradeSignals.fulfilled, (state, action) => {
        state.loading.recentSignals = false;
        state.recentSignals = action.payload.signals || [];
        state.lastFetch.recentSignals = new Date().toISOString();
      })
      .addCase(fetchUserRecentTradeSignals.rejected, (state, action) => {
        state.loading.recentSignals = false;
        state.error.recentSignals = action.payload;
      });

    // Fetch user trade signals by symbol
    builder
      .addCase(fetchUserTradeSignalsBySymbol.pending, (state) => {
        state.loading.symbolSignals = true;
        state.error.symbolSignals = null;
      })
      .addCase(fetchUserTradeSignalsBySymbol.fulfilled, (state, action) => {
        state.loading.symbolSignals = false;
        const { symbol, signals } = action.payload;
        state.symbolSignals[symbol] = signals || [];
        state.lastFetch.symbolSignals = new Date().toISOString();
      })
      .addCase(fetchUserTradeSignalsBySymbol.rejected, (state, action) => {
        state.loading.symbolSignals = false;
        state.error.symbolSignals = action.payload;
      });
  }
});

export const { clearError, clearSignals, setFilters } = userTradeSignalsSlice.actions;

// Selectors
export const selectUserTradeSignals = (state) => state.userTradeSignals.signals;
export const selectUserActiveTradeSignals = (state) => state.userTradeSignals.activeSignals;
export const selectUserRecentTradeSignals = (state) => state.userTradeSignals.recentSignals;
export const selectUserTradeSignalStats = (state) => state.userTradeSignals.stats;
export const selectUserTradeSignalSummary = (state) => state.userTradeSignals.summary;
export const selectUserTradeSignalPagination = (state) => state.userTradeSignals.pagination;
export const selectUserTradeSignalFilters = (state) => state.userTradeSignals.filters;
export const selectUserTradeSignalTableStats = (state) => state.userTradeSignals.tableStats;
export const selectUserTradeSignalTotal = (state) => state.userTradeSignals.total;
export const selectUserTradeSignalsLoading = (state) => state.userTradeSignals.loading;
export const selectUserTradeSignalsError = (state) => state.userTradeSignals.error;
export const selectUserTradeSignalsBySymbol = (state, symbol) => 
  state.userTradeSignals.symbolSignals[symbol] || [];

export default userTradeSignalsSlice.reducer;
